// @flow

import _ from 'lodash/fp';
import EventEmitter from 'events';

// Application infrastructure
import { Route, Router } from 'app/rgs/routing';
import type { IDependencyResolver } from 'app/rgs/annotation/di';

// types
import type { Modules } from 'app/rgs/annotation/module';
import type {
	TRouteParts,
	TRoutePaths,
	TRouterConfigOptions,
	TRouteConfigParams,
} from '../annotation/route-config';
import type { RouteHandlerResult } from 'app/rgs/routing/contracts';

export const ROUTE_CHANGES = '__route-changes';

export class AbstractRouteConfig extends EventEmitter {
	_router: Router<*, *, *>;
	_modules: Modules;
	_routes: TRoutePaths;
	_params: TRouteConfigParams;
	_dependencyResolver: IDependencyResolver;

	get DEFAULT_MODULE_NAME (): string {
		return this._params.DEFAULT_MODULE;
	}

	constructor (
		dependencyResolver: IDependencyResolver,
		router: Router<*, *, *>,
		options?: TRouterConfigOptions,
	) {
		super(...arguments);

		this._dependencyResolver = dependencyResolver;
		this._router = router;

		const { routeModules, routes, params } = _.defaults({
			routeModules: {},
			routes: [],

			params: {
				DEFAULT_MODULE: '',
			},
		})(options || {});

		this._modules = routeModules;
		this._routes = routes;
		this._params = params;
	}

	getRoutes (): Route[] {
		return this._routes.map(route => {
			const watching = _.compose(
				this._mountController(),
				this.saySubscribersAboutRouteChanges(route.name),
				this.connectModuleByRouteParts(),
				this.selectRouteParts(route.handler, route.name, this._router),
			);

			return new Route(route.name, route.urlPattern, watching, route.filters);
		});
	}

	_mountController (): any {
		return (params: { routeParts: TRouteParts, module: * }) => {
			const { module: newModule } = params;

			if (!newModule) {
				return;
			}

			return this._dependencyResolver.createController(newModule);
		};
	}

	saySubscribersAboutRouteChanges (routeName: string) {
		return (params: { routeParts: RouteHandlerResult, module: * }) => {
			const { routeParts } = params;

			this._router.setRouteParts(routeName, routeParts);

			this.sayAboutChanges(routeParts);

			return params;
		};
	}

	connectModuleByRouteParts () {
		return (routeParts: TRouteParts) => {
			const module = this.getLoadableModuleWhenSpecifiedRoute(routeParts);

			return { routeParts, module };
		};
	}

	selectRouteParts (routeHandler: (string[], string, Router<*, *, *>)
		=> TRouteParts, routeName: string, router: Router<*, *, *>): any {
		return (args: Object): TRouteParts => {
			const routeParts = routeHandler(args, routeName, router);

			return routeParts;
		};
	}

	getLoadableModuleWhenSpecifiedRoute (params: TRouteParts): any {
		const moduleName = this.getModuleIdentifier(params) || '';
		const DEFAULT_MODULE = this.DEFAULT_MODULE_NAME;

		const module = this._modules[moduleName] || this._modules[DEFAULT_MODULE];

		return module;
	}

	//may be override too
	sayAboutChanges (routeHandlerResult: RouteHandlerResult) {
		this.emit(ROUTE_CHANGES, routeHandlerResult);
	}

	//OVERRIDE ME!
	//eslint-disable-next-line no-unused-vars
	getModuleIdentifier (routeParts: TRouteParts): ?string {
		throw new Error('[Route-Config] Method - "getModuleIdentifier" is abstract');
	}
}
